/**
 * CorrectionDialog - Storno-Dialog für FINAL-gesperrte Einträge
 * 
 * Nur für Admin und Accounting sichtbar.
 * Ermöglicht:
 * 1. Nur stornieren (Gegeneintrag mit negativen Werten)
 * 2. Stornieren und korrigiert neu anlegen
 */
import { useState, useEffect } from 'react';
import { Ban, ArrowRight, AlertTriangle, FileText, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { safeFormatDate } from '../lib/dateTime';
import { getErrorMessage } from '../lib/errorMessage';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const MINIMUM_REASON_LENGTH = 5;

export const CorrectionDialog = ({
  open,
  onOpenChange,
  entry,
  collectionName = 'work_time',
  onSuccess,
}) => {
  const { api } = useAuth();
  const [mode, setMode] = useState('storno_only'); // 'storno_only' | 'storno_and_correct'
  const [reason, setReason] = useState('');
  const [correctedValues, setCorrectedValues] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize corrected values from original entry
  useEffect(() => {
    if (entry && open) {
      setCorrectedValues({
        start_time: entry.start_time || '',
        end_time: entry.end_time || '',
        break_minutes: entry.break_minutes || 0,
        hours: entry.hours || 0,
        description: entry.description || '',
        kilometers: entry.kilometers || 0,
      });
    }
  }, [entry, open]);

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      setMode('storno_only');
      setReason('');
      setCorrectedValues({});
    }
  }, [open]);

  const isReasonValid = reason.trim().length >= MINIMUM_REASON_LENGTH;

  const handleSubmit = async () => {
    if (!isReasonValid || !entry) return;

    setIsSubmitting(true);
    try {
      const payload = {
        collection_name: collectionName,
        document_id: entry.id,
        reason: reason.trim(),
        new_values: mode === 'storno_and_correct' ? correctedValues : null,
      };

      const response = await api.post('/locks/corrections', payload);

      const stornoHours = Math.abs(entry.hours || 0);
      toast.success('Storno wurde erstellt', {
        description: `Gegeneintrag mit -${stornoHours.toFixed(2)}h angelegt${
          mode === 'storno_and_correct' ? ' und korrigierter Eintrag erstellt' : ''
        }`,
      });

      onSuccess?.(response.data);
      onOpenChange(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Fehler beim Erstellen des Stornos'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    return safeFormatDate(dateStr, 'EEEE, d. MMMM yyyy', '');
  };

  if (!entry) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Ban className="w-5 h-5 text-red-600 dark:text-red-400" />
            Storno erstellen
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Der Originaleintrag bleibt unverändert. Es wird ein Gegeneintrag erzeugt.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Warning */}
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Der ursprüngliche Eintrag wurde bereits abgerechnet oder an DATEV übergeben 
                  und kann nicht mehr geändert werden.
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300/80 mt-1">
                  Es wird ein Gegeneintrag erzeugt, der ihn rechnerisch aufhebt.
                </p>
              </div>
            </div>
          </div>

          {/* Original entry info */}
          <div className="p-4 rounded-lg bg-muted/50 border border-border">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">Originaleintrag</span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Datum:</span>
                <span className="text-foreground ml-2">{formatDate(entry.date)}</span>
              </div>
              {entry.hours !== undefined && (
                <div>
                  <span className="text-muted-foreground">Stunden:</span>
                  <span className="text-foreground ml-2">{Number(entry.hours).toFixed(2)}h</span>
                </div>
              )}
              {entry.start_time && entry.end_time && (
                <div>
                  <span className="text-muted-foreground">Zeit:</span>
                  <span className="text-foreground ml-2">{entry.start_time} - {entry.end_time}</span>
                </div>
              )}
              {entry.kilometers !== undefined && (
                <div>
                  <span className="text-muted-foreground">Kilometer:</span>
                  <span className="text-foreground ml-2">{entry.kilometers} km</span>
                </div>
              )}
              {entry.project_name && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">Auftrag:</span>
                  <span className="text-foreground ml-2">{entry.project_name}</span>
                </div>
              )}
            </div>
            
            {/* Storno preview */}
            <div className="mt-3 pt-3 border-t border-border">
              <div className="flex items-center gap-2">
                <ArrowRight className="w-4 h-4 text-red-600 dark:text-red-400" />
                <span className="text-sm text-red-600 dark:text-red-400">
                  Storno-Eintrag: 
                  {entry.hours !== undefined && ` -${Math.abs(entry.hours).toFixed(2)}h`}
                  {entry.kilometers !== undefined && ` -${Math.abs(entry.kilometers)} km`}
                </span>
              </div>
            </div>
          </div>

          {/* Mode selection */}
          <div>
            <Label className="text-muted-foreground mb-3 block">Aktion</Label>
            <RadioGroup 
              value={mode} 
              onValueChange={setMode}
              className="space-y-2"
            >
              <label className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border cursor-pointer hover:bg-muted/50 transition-colors">
                <RadioGroupItem value="storno_only" id="storno_only" />
                <div>
                  <span className="text-foreground font-medium">Nur stornieren</span>
                  <p className="text-sm text-muted-foreground">
                    Erstellt nur den Gegeneintrag
                  </p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border cursor-pointer hover:bg-muted/50 transition-colors">
                <RadioGroupItem value="storno_and_correct" id="storno_and_correct" />
                <div>
                  <span className="text-foreground font-medium">Stornieren und korrigiert neu anlegen</span>
                  <p className="text-sm text-muted-foreground">
                    Erstellt Gegeneintrag und einen neuen korrigierten Eintrag
                  </p>
                </div>
              </label>
            </RadioGroup>
          </div>

          {/* Corrected values (only if mode is storno_and_correct) */}
          {mode === 'storno_and_correct' && (
            <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
              <h4 className="text-sm font-medium text-emerald-600 dark:text-emerald-400 mb-3 flex items-center gap-2">
                <Undo2 className="w-4 h-4" />
                Korrigierte Werte
              </h4>
              <div className="grid grid-cols-2 gap-4">
                {collectionName === 'work_time' && (
                  <>
                    <div>
                      <Label className="text-muted-foreground text-xs">Startzeit</Label>
                      <Input
                        type="time"
                        value={correctedValues.start_time || ''}
                        onChange={(e) => setCorrectedValues(prev => ({ ...prev, start_time: e.target.value }))}
                        className="bg-muted border-border text-foreground mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Endzeit</Label>
                      <Input
                        type="time"
                        value={correctedValues.end_time || ''}
                        onChange={(e) => setCorrectedValues(prev => ({ ...prev, end_time: e.target.value }))}
                        className="bg-muted border-border text-foreground mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Pause (Minuten)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={correctedValues.break_minutes || 0}
                        onChange={(e) => setCorrectedValues(prev => ({ ...prev, break_minutes: parseInt(e.target.value) || 0 }))}
                        className="bg-muted border-border text-foreground mt-1"
                      />
                    </div>
                  </>
                )}
                {collectionName === 'mileage_entries' && (
                  <div>
                    <Label className="text-muted-foreground text-xs">Kilometer</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      value={correctedValues.kilometers || 0}
                      onChange={(e) => setCorrectedValues(prev => ({ ...prev, kilometers: parseFloat(e.target.value) || 0 }))}
                      className="bg-muted border-border text-foreground mt-1"
                    />
                  </div>
                )}
                {(collectionName === 'project_time' || collectionName === 'mileage_entries') && (
                  <div className="col-span-2">
                    <Label className="text-muted-foreground text-xs">Beschreibung</Label>
                    <Input
                      value={correctedValues.description || ''}
                      onChange={(e) => setCorrectedValues(prev => ({ ...prev, description: e.target.value }))}
                      className="bg-muted border-border text-foreground mt-1"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Reason input */}
          <div>
            <Label className="text-muted-foreground mb-2 block">
              Begründung <span className="text-red-600 dark:text-red-400">*</span>
            </Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Grund für den Storno (mind. 5 Zeichen)"
              className="bg-input border-border text-foreground min-h-[80px] resize-none"
              data-testid="storno-reason-input"
            />
            <p className={`text-xs mt-1 ${reason.length >= MINIMUM_REASON_LENGTH ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
              {reason.length} / {MINIMUM_REASON_LENGTH} Zeichen
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-muted-foreground"
            disabled={isSubmitting}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isReasonValid || isSubmitting}
            className="bg-red-500 hover:bg-red-600 text-white"
            data-testid="confirm-storno-btn"
          >
            {isSubmitting ? 'Wird erstellt...' : 'Storno erstellen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CorrectionDialog;
